import {
  isCondition,
  isDayOnly,
  isGroup,
  operatorDefinition,
  type FieldDefinition,
  type Filter,
  type FilterCondition,
  type FilterGroup,
  type Json,
} from "@estelle/domain";

import { showTime, splitDayAndTime } from "@/lib/store-time";

/**
 * The filter's conditions, when it is a flat AND of them — otherwise null.
 *
 * A chip row is flat and `Filter` is a tree, so the two only correspond when
 * the root is a single AND group of plain conditions. That covers everything
 * the builder is normally used for, including "Jaipur or Mumbai", which is
 * `is_any_of` on one field rather than an OR of two conditions.
 *
 * Null is the honest answer for anything else. Flattening a nested filter
 * into chips would put a row on screen that states something the filter does
 * not say, which is worse than the "Filter (3)" count chips replace — a count
 * is merely uninformative, a wrong chip is misleading.
 */
export function flatConditions(filter: Filter): FilterCondition[] | null {
  if (filter.combinator !== "and") return null;
  if (!filter.children.every(isCondition)) return null;
  return filter.children;
}

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

/**
 * One value, as a person would say it.
 *
 * Money is the only type formatted, because it is the only one where the raw
 * value is actively misleading — "Total ≥ 1000" and "Total ≥ ₹1,000" differ by
 * whether the reader has to guess the unit. Dates are left as stored: the
 * chip shows what the filter holds, and a shared component inventing a date
 * format would disagree with whatever each module's table already uses.
 */
function formatValue(value: Json, field: FieldDefinition): string {
  if (Array.isArray(value)) {
    return value.map((item) => formatValue(item, field)).join(", ");
  }
  // `Json` admits an object because a condition value is stored as `jsonb`;
  // no operator arity produces one, so this is a guard rather than a case.
  if (value === null || typeof value === "object") return "—";
  if (field.type === "money" && typeof value === "number") {
    return INR.format(value);
  }

  /*
    The one date that is not left as stored: a datetime with a time in it
    reads `2026-07-13T09:00` as held (#555), and the picker it came from said
    9:00 am. The chip says the time the way the picker and the table do.
  */
  if (
    field.type === "datetime" &&
    typeof value === "string" &&
    !isDayOnly(value)
  ) {
    const { day, time } = splitDayAndTime(value);
    if (day !== "" && time !== "") return `${day} ${showTime(time)}`;
  }

  /*
    An enum stores `pending` and displays "Pending". Showing the stored value
    would put the chip and the column it filters into different vocabularies
    on one screen, so a chip would read "Payment status is any of pending" over
    a table of rows saying "Pending".

    Falling back to the raw value covers a `reference` field whose options load
    lazily and a stale saved view naming an option that no longer exists —
    both better shown as-is than as an em dash.
  */
  const option = field.options?.find((o) => o.value === value);
  if (option) return option.label;

  return String(value);
}

/** A condition split at the seam the filter bar chip renders differently. */
export interface ConditionPhrase {
  /** "Field is" — the part shown at the chip's normal weight. */
  head: string;
  /** The value half, or null for `is empty` and the like, which have none. */
  value: string | null;
}

/**
 * A condition as `head` and `value`, split so a chip can emphasise the value
 * the way the reference bar does — "Order status **is** **Open**" reads as a
 * sentence with the answer picked out, not a flat label.
 *
 * Both halves come from declarations that already exist — the field's `label`
 * and the operator's `label` from `OPERATORS_BY_FIELD_TYPE`. No second
 * vocabulary to write or keep in sync, which is also why an operator relabelled
 * in the domain changes the chip without anyone remembering to come here.
 *
 * Null where the field is unknown to this section: a saved view written before
 * a field was renamed holds a name nothing matches, and `applyColumns` already
 * drops those rather than rendering a placeholder. A chip does the same.
 */
export function describeConditionParts(
  condition: FilterCondition,
  fields: FieldDefinition[],
): ConditionPhrase | null {
  const field = fields.find((f) => f.name === condition.field);
  if (!field) return null;

  const operator = operatorDefinition(field.type, condition.operator);
  if (!operator) return null;

  const head = `${field.label} ${operator.label}`;

  if (operator.arity === "none") return { head, value: null };

  if (operator.arity === "two" && Array.isArray(condition.value)) {
    const [from, to] = condition.value;
    return {
      head,
      value: `${formatValue(from ?? "", field)} and ${formatValue(to ?? "", field)}`,
    };
  }

  if (condition.value === undefined) return { head, value: null };
  return { head, value: formatValue(condition.value, field) };
}

/**
 * A condition as one phrase: "Total ≥ ₹1,000", "Tracking number is empty".
 *
 * Everywhere that only needs the sentence, not the seam `describeConditionParts`
 * exists for — plain text, a tooltip, a CSV.
 */
export function describeCondition(
  condition: FilterCondition,
  fields: FieldDefinition[],
): string | null {
  const parts = describeConditionParts(condition, fields);
  if (!parts) return null;
  return parts.value === null ? parts.head : `${parts.head} ${parts.value}`;
}

/**
 * A group as one phrase, its conditions joined by its own AND or OR: "Shipped
 * is between 2026-09-30 and 2026-09-30", or "Total ≥ ₹1,000 or Tags has VIP".
 *
 * What the filter bar's group chip shows, so a filter with a group in it can
 * be read off the bar rather than being "2 conditions (advanced)". A group
 * inside it is bracketed, and a condition on a field this section no longer
 * declares is left out, as a chip leaves it out.
 */
export function describeGroup(
  group: FilterGroup,
  fields: FieldDefinition[],
): string {
  const parts = group.children.flatMap((child) => {
    if (isGroup(child)) {
      const inner = describeGroup(child, fields);
      return inner === "" ? [] : [`(${inner})`];
    }
    const text = describeCondition(child, fields);
    return text === null ? [] : [text];
  });
  return parts.join(group.combinator === "and" ? " and " : " or ");
}
